import {
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
} from '@reduxjs/toolkit';
import { RootState } from '../..';
import { adminGenerateSitemap, adminGetSitemaps } from './adminApi';
import { IUser } from '../../UserSlice';

export const SITEMAP_STATUS = {
  PROCESSING: 'PROCESSING',
  COMPLETED: 'COMPLETED',
} as const;

export type TSitemapStatus = typeof SITEMAP_STATUS[keyof typeof SITEMAP_STATUS];

export interface ISitemap {
  _id?: string;
  name: string;
  path: string;
  urlCount: number;
  status: TSitemapStatus;
  generatedBy?: IUser;
  createdAt: string;
}

export interface IInitialSitemapState {
  status: 'idle' | 'loading' | 'rejected';
  // Admin
  adminSitemapListData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<ISitemap>;
  };
}

const initialState: IInitialSitemapState = {
  status: 'idle',
  // Admin
  adminSitemapListData: undefined,
};

export const SitemapSlice = createSlice({
  initialState,
  name: 'sitemap',
  reducers: {
    resetAdminSitemapListData: (state) => {
      state.adminSitemapListData = undefined;
    },
  },
  extraReducers(builder) {
    builder.addCase(adminGetSitemaps.fulfilled, (state, action) => {
      const result = action.payload;

      const newAdminSitemapListData = {
        limit: result.limit,
        offset: result.offset,
        totalDocs: result.totalDocs || 0,
        list: result.docs || [],
      };

      state.adminSitemapListData = newAdminSitemapListData;
    });

    // Common Matchers
    builder.addMatcher(
      isPending(adminGenerateSitemap, adminGetSitemaps),
      (state) => {
        state.status = 'loading';
      }
    );

    builder.addMatcher(
      isFulfilled(adminGenerateSitemap, adminGetSitemaps),
      (state) => {
        state.status = 'idle';
      }
    );

    builder.addMatcher(
      isRejected(adminGenerateSitemap, adminGetSitemaps),
      (state) => {
        state.status = 'rejected';
      }
    );
  },
});

export const getSitemapState = (state: RootState): RootState['sitemap'] =>
  state.sitemap;

export const { resetAdminSitemapListData } = SitemapSlice.actions;

export * from './adminApi';
export default SitemapSlice.reducer;
